import { Flex, Heading, Link } from '@adobe/react-spectrum';
import Close from '@spectrum-icons/workflow/Close';
import qs from 'query-string';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { commonApi, handleApiCall } from '@exchange-frontends/api';
import { useHandleApiCall } from '@exchange-frontends/custom-hooks';
import { sendDataToLaunch } from '@exchange-frontends/utils';

import { useStore } from '../../store';
import { appTypeFilters } from '../search-sidebar/SideBar';

const FilterItem = styled.span`
  background-color: var(--spectrum-global-color-gray-50);
  border: 1px solid var(--spectrum-global-color-gray-600);
  border-radius: 4px;
  color: var(--spectrum-global-color-gray-700);
  display: inline-flex;
  font-size: var(--spectrum-global-dimension-font-size-75);
  align-items: center;
  padding: 4px 0 4px 10px;
  margin: 1px 8px 1px 0;
`;

const FilterDeleteButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      padding: 0,
      backgroundColor: 'transparent',
      border: '0',
    }}
  >
    <Close size="XS" marginX="8.5px" />
  </button>
);

const getCountriesHandler = handleApiCall(commonApi.getCountries);
const getIndustriesHandler = handleApiCall(commonApi.getIndustries);

const SelectedFilters = ({ filterKeys }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const cloud = useStore((state) => state.cloud);
  const productsList = useStore((store) => store.productsList);

  const { response: countries, loading: countriesLoading } = useHandleApiCall({
    shouldFetch: Boolean(cloud && cloud !== 'cc'),
    handler: getCountriesHandler,
    shouldShowError: false,
  });

  const { response: industries, loading: industriesLoading } = useHandleApiCall({
    shouldFetch: Boolean(cloud && cloud !== 'cc'),
    handler: getIndustriesHandler,
    shouldShowError: false,
  });

  const filters = useMemo(() => {
    if (countriesLoading || industriesLoading) return [];
    const parsedSearch = qs.parse(location.search);
    const filteredKeys = Object.entries(parsedSearch ?? {})
      .filter(([key, value]) => filterKeys?.includes(key) && value !== 'All')
      .map(([key, value]) => {
        const getFilterLabelFromStaticData = (filterValue) => {
          const staticFilterList = { country: countries, industry: industries, appType: appTypeFilters };
          return staticFilterList[key]?.find((item) => item?.value === filterValue)?.label ?? filterValue;
        };
        if (Array.isArray(value)) {
          return [key, value.map((val) => getFilterLabelFromStaticData(val))];
        } else {
          return [key, getFilterLabelFromStaticData(value)];
        }
      });
    return filteredKeys;
  }, [countriesLoading, industriesLoading, location.search, filterKeys, countries, industries]);

  const removeFilter = (key, value) => {
    const parsedSearch = qs.parse(location.search);
    if (typeof parsedSearch[key] !== 'object') {
      delete parsedSearch[key];
    } else {
      const staticFilterList = { country: countries, industry: industries, appType: appTypeFilters };
      const urlValue = staticFilterList[key]?.find((item) => item?.label === value)?.value ?? value;
      parsedSearch[key] = parsedSearch[key].filter((v) => v !== urlValue);
    }
    parsedSearch.page = 1;
    navigate({
      search: qs.stringify(Object.assign({}, parsedSearch)),
    });
  };

  const removeAllFilters = () => {
    const parsedSearch = qs.parse(location.search);
    for (const filterKey of filterKeys) {
      delete parsedSearch[filterKey];
    }
    parsedSearch.page = 1;
    sendDataToLaunch('filters', 'clearFilter');
    navigate({
      search: qs.stringify(Object.assign({}, parsedSearch)),
    });
  };

  return filters.length && !countriesLoading && !industriesLoading ? (
    <Flex alignItems="center" wrap="wrap" data-testid="selected-filters">
      <Heading level={4} marginEnd="8px" marginY="0">
        Filters:
      </Heading>
      <Flex wrap="wrap">
        {filters.map(([key, urlValue]) => {
          if (typeof urlValue !== 'object') {
            const value =
              key === 'product'
                ? productsList?.find((product) => product?.key === urlValue)?.name ?? urlValue
                : urlValue;
            return (
              <FilterItem key={key + value}>
                {value} <FilterDeleteButton onClick={() => removeFilter(key)} />
              </FilterItem>
            );
          } else {
            const value =
              key === 'product'
                ? urlValue.map(
                    (productKey) => productsList?.find((product) => product?.key === productKey)?.name ?? productKey
                  )
                : urlValue;
            return value.map((v) => (
              <FilterItem key={key + v}>
                {v}{' '}
                <FilterDeleteButton
                  onClick={() =>
                    removeFilter(key, key === 'product' ? productsList?.find((product) => product?.name === v)?.key : v)
                  }
                />
              </FilterItem>
            ));
          }
        })}
      </Flex>
      <Link onPress={removeAllFilters}>Clear all</Link>
    </Flex>
  ) : null;
};

export default SelectedFilters;
